import React, { useRef, useEffect, useState } from "react";
import axios from 'axios';
import {
  makeStyles,
  Paper,
  Button,
  Divider,
  Typography,
  IconButton,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Dialog,
  DialogContent,
  Stepper,
  Step,
  StepLabel,
  StepContent,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import WarningIcon from "@material-ui/icons/Warning";
import Dropzone from "react-dropzone";
import clsx from "clsx";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import { getLang } from "app/feature/constants";
import InvalidSerialsTableComponent from "./InvalidSerialsTable.component";

const useStyles = makeStyles((theme) => ({
  root: {
    width: 650,
    [theme.breakpoints.down("xs")]: {
      width: "100%",
    },
  },
  header: {
    padding: "4px 0px",
    paddingLeft: theme.spacing(3),
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  form: {
    maxHeight: "65vh",
    overflow: "auto",
    padding: theme.spacing(2),
  },
  fileInput: {
    border: "1px dashed rgba(0, 0, 0, 0.12)",
    borderRadius: 4,
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    cursor: "pointer",
    backgroundColor: "rgba(0, 0, 0, 0.04)",
  },
  activeDropzone: {
    borderColor: theme.palette.primary.main,
    backgroundColor: "rgba(0, 128, 255, 0.08)",
  },
  publishButton: {
    color: theme.palette.common.white,
  },
  caption: {
    color: "rgba(0, 0, 0, 0.54)",
  },
  table: {
    minWidth: 450,
  },
  errorCell: {
    backgroundColor: "#ffebee !important",
    borderColor: "#f44336 !important",
  },
  templateOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1600,
    backdropFilter: 'blur(4px)',
  },
  templateCard: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    borderRadius: 20,
    padding: theme.spacing(4),
    minWidth: 450,
    maxWidth: 500,
    margin: theme.spacing(2),
    position: 'relative',
    overflow: 'hidden',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
    animation: '$slideInScale 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
    [theme.breakpoints.down('xs')]: {
      minWidth: 'calc(100vw - 32px)',
      margin: theme.spacing(2),
    },
  },
  '@keyframes slideInScale': {
    '0%': {
      opacity: 0,
      transform: 'scale(0.8) translateY(30px)',
    },
    '100%': {
      opacity: 1,
      transform: 'scale(1) translateY(0)',
    },
  },
  templateCardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(3),
  },
  templateCardTitle: {
    fontSize: '1.5rem',
    fontWeight: 600,
    color: 'white',
    textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
    letterSpacing: '0.5px',
  },
  templateCloseButton: {
    color: 'white',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
    },
  },
  templateContent: {
    color: 'white',
  },
  templateInstructions: {
    fontSize: '1rem',
    lineHeight: 1.6,
    marginBottom: theme.spacing(3),
    color: 'rgba(255, 255, 255, 0.9)',
    textAlign: 'center',
  },
  templateDownloadSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  downloadButton: {
    background: 'linear-gradient(45deg, #FF6B6B, #FF8E53)',
    border: 'none',
    borderRadius: 25,
    color: 'white',
    padding: theme.spacing(1.5, 4),
    fontSize: '1rem',
    fontWeight: 600,
    textTransform: 'none',
    boxShadow: '0 8px 16px rgba(255, 107, 107, 0.3)',
    transition: 'all 0.3s ease',
    minWidth: 200,
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 12px 24px rgba(255, 107, 107, 0.4)',
      background: 'linear-gradient(45deg, #FF5252, #FF7043)',
    },
    '&:active': {
      transform: 'translateY(0)',
    },
  },
  templateBackButton: {
    backgroundColor: 'transparent',
    color: 'rgba(255, 255, 255, 0.8)',
    border: '2px solid rgba(255, 255, 255, 0.3)',
    borderRadius: 20,
    padding: theme.spacing(1, 3),
    textTransform: 'none',
    fontWeight: 500,
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      color: 'white',
      borderColor: 'rgba(255, 255, 255, 0.5)',
    },
  },
  templateDecoration: {
    position: 'absolute',
    top: -50,
    right: -50,
    width: 150,
    height: 150,
    background: 'linear-gradient(45deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05))',
    borderRadius: '50%',
    opacity: 0.6,
  },
  templateDecoration2: {
    position: 'absolute',
    bottom: -30,
    left: -30,
    width: 100,
    height: 100,
    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0.03))',
    borderRadius: '50%',
    opacity: 0.8,
  },
  templateDialog: {
    padding: theme.spacing(2),
  },
  templateTitle: {
    fontSize: '0.95rem',
    fontWeight: 500,
    marginBottom: theme.spacing(1.5),
    color: theme.palette.text.primary,
    textTransform: 'uppercase',
  },
  downloadTemplateButton: {
    backgroundColor: theme.palette.grey[700],
    color: theme.palette.common.white,
    textTransform: 'uppercase',
    padding: theme.spacing(1, 3),
    fontSize: '0.875rem', 
    '&:hover': {
      backgroundColor: theme.palette.grey[800],
    }
  },
  backButton: {
    textTransform: 'uppercase',
    fontSize: '0.875rem',
  },
  dialogTitle: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1.5, 2),
    '& h2': {
      fontSize: '1rem',
      fontWeight: 500,
    }
  },
  dialogActions: {
    padding: theme.spacing(1, 2, 1.5),
  },
  templateDialogPaper: {
    borderRadius: 16,
    overflow: 'hidden',
    minWidth: 480,
    maxWidth: 520,
    background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
    [theme.breakpoints.down('xs')]: {
      minWidth: 'calc(100vw - 32px)',
      margin: theme.spacing(2),
    },
  },
  templateDialogTitle: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    padding: theme.spacing(3, 3, 2),
    textAlign: 'center',
    position: 'relative',
    '& h2': {
      fontSize: '1.5rem',
      fontWeight: 600,
      textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
      letterSpacing: '0.5px',
      margin: 0,
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      height: 3,
      background: 'linear-gradient(90deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.3) 100%)',
    },
  },
  templateDialogContent: {
    padding: theme.spacing(4, 3),
    textAlign: 'center',
    background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
  },
  templateDialogInstructions: {
    fontSize: '1rem',
    lineHeight: 1.6,
    marginBottom: theme.spacing(4),
    color: '#555',
    maxWidth: 420,
    margin: '0 auto',
  },
  templateDialogDownloadButton: {
    background: 'linear-gradient(45deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    borderRadius: 25,
    padding: theme.spacing(1.5, 4),
    fontSize: '1rem',
    fontWeight: 600,
    textTransform: 'none',
    boxShadow: '0 8px 16px rgba(102, 126, 234, 0.3)',
    border: 'none',
    minWidth: 200,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 12px 24px rgba(102, 126, 234, 0.4)',
      background: 'linear-gradient(45deg, #5a67d8 0%, #6b46c1 100%)',
    },
    '&:active': {
      transform: 'translateY(0)',
    },
  },
  templateDialogActions: {
    padding: theme.spacing(2, 3, 3),
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
  },
  templateDialogBackButton: {
    color: '#666',
    borderColor: '#ddd',
    borderRadius: 20,
    padding: theme.spacing(1, 3),
    textTransform: 'none',
    fontWeight: 500,
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: 'rgba(102, 126, 234, 0.1)',
      borderColor: '#667eea',
      color: '#667eea',
    },
  },
  templateDialogCloseButton: {
    position: 'absolute',
    top: theme.spacing(2),
    right: theme.spacing(2),
    color: 'white',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    backdropFilter: 'blur(10px)',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.3)',
    },
  },
}));

export default function ImportPresetWinnersDialogComponent({
  isOpen,
  handleClose,
  lang,
  addAlert,
  setFile,
  file,
  handleFileChange,
  showTemplateInput,
  setShowTemplateInput,
  handleValidate,
  tableHeader,
  tableValues,
  errorField,
  errorTitles,
  showPreview,
  isValidFile,
  handleSubmit,
  isValidating,
  isSubmitting,
  isFileProcessing,
  luckyDrawUuid,
  prizeId,
  invalidSerialNumbers,
  validSerialNumbers,
  allSerialsStatus,
  showInvalidSerialsTable,
}) {

  const classes = useStyles();
  const dropzoneRef = useRef();
  const [activeStep, setActiveStep] = useState(0);
  const [manualNavigation, setManualNavigation] = useState(false);

  useEffect(() => {
    // Reset stepper when dialog opens
    if (isOpen) {
      setActiveStep(0);
      setManualNavigation(false);
    }
  }, [isOpen, lang]);

  // Auto-advance to validate step when file is uploaded and processed
  useEffect(() => {
    if (file && !isFileProcessing && activeStep === 1 && !manualNavigation) {
      setActiveStep(2); // Move to validate step
    }
  }, [file, isFileProcessing, activeStep, manualNavigation]);

  // Define the steps for the stepper
  const steps = [
    {
      label: getLang(lang, "label.GET_TEMPLATE"),
      description: getLang(lang, "paragraph.TEMPLATE_DOWNLOAD_FILL_IN_PROMPTS_LUCKYDRAW"),
    },
    {
      label: getLang(lang, "label.UPLOAD_XLSX_FILE"),
      description: getLang(lang, "label.XLSX_DROP_OR_CLICK"),
    },
    {
      label: getLang(lang, "label.VALIDATE"),
      description: getLang(lang, "paragraph.VALIDATE_TIPS"),
    },
  ];

  // Step navigation functions
  const handleNext = () => {
    setManualNavigation(true);
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setManualNavigation(true);
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };



  // Determine if we can proceed to next step
  const canProceedToStep = (stepIndex) => {
    switch (stepIndex) {
      case 1: // Can proceed to Upload if template discussion is done
        return true;
      case 2: // Can proceed to Validate if file is uploaded
        return !!file;
      default:
        return true;
    }
  };

  const getPreview = () => {
    if (!tableValues || !errorField) return tableValues.slice(0, 5);
    const start = Math.max(0, errorField.row - 3);
    const end = Math.min(tableValues.length, errorField.row + 2);
    return tableValues.slice(start, end);
  };

  // Calculate current row for highlighting errors in preview
  const errorRow = errorField ? errorField.row - Math.max(0, errorField.row - 3) : 0;

  // Handle template download
  const handleTemplateDownload = async () => {
    try {
      const response = await axios({
        url: '/campaignV2/customer/luckyDraw/winnerTemplate',
        method: 'GET',
        params: {
          luckyDrawUuid: luckyDrawUuid,
          prizeId: prizeId,
        },
        responseType: 'blob',
      });

      // Extract filename from Content-Disposition header
      const contentDisposition = response.headers['content-disposition'];
              let filename = 'preset_winners_template.xlsx'; // fallback
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/); 
        if (filenameMatch) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }

      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);

      addAlert({
        severity: "success",
        message: "Template download initiated. Please check your browser."
      });

    } catch (error) {
      let errorMessageString = "An unexpected error occurred while trying to download the template.";

      if (error.response && error.response.data) {
        // Handle if error.response.data is a Blob
        if (error.response.data instanceof Blob) {
          try {
            const errorText = await error.response.data.text();
            const parsedError = JSON.parse(errorText);
            if (parsedError && typeof parsedError.message === 'string') {
              errorMessageString = parsedError.message;
            } else if (typeof errorText === 'string' && errorText.trim() !== '') {
              errorMessageString = errorText; // Fallback to full blob text if parsing fails or no message field
            }
          } catch (e) {
            errorMessageString = "Failed to parse error response from server.";
          }
        } 
        // Handle if error.response.data is an object
        else if (typeof error.response.data.message === 'string') {
          errorMessageString = error.response.data.message;
        } else if (typeof error.response.data === 'string') {
            errorMessageString = error.response.data;
        }
      } else if (typeof error.message === 'string') { // General axios or network error
        errorMessageString = error.message;
      }
      
      addAlert({
        severity: "error",
        message: errorMessageString,
      });
    }
  };

  if (showTemplateInput) {
    return (
      <Dialog
        open={isOpen}
        onClose={() => setShowTemplateInput(false)}
        maxWidth="md"
        fullWidth
        aria-labelledby="template-download-dialog"
        style={{
          zIndex: 1600,
        }}
        BackdropProps={{
          style: {
            backgroundColor: "rgba(0, 0, 0, 0.6)",
          },
        }}
      >
        <DialogContent style={{ padding: 0 }}>
          <Paper elevation={0} style={{ 
            maxHeight: "90vh",
            overflow: "auto",
            border: "1px solid #ccc",
            borderRadius: "4px",
          }}>
            <div className={classes.header}>
              <Typography variant="subtitle2" style={{ fontWeight: "bold" }}>
                {getLang(lang, "label.DOWNLOAD_TEMPLATE")}
              </Typography>
              <IconButton color="primary" onClick={() => setShowTemplateInput(false)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </div>
            <Divider />

            <div className={classes.form}>
              <Box mb={3}>
                <Paper elevation={0}>
                  <Box display="flex" p={2}>
                    <Box minWidth={70}>
                      <Typography variant="body1">
                        <b>{getLang(lang, "label.STEP_1")}</b>
                      </Typography>
                    </Box>
                    <Box>
                      <Box mb={1}>
                        <Typography variant="body1">
                          <b>{getLang(lang, "label.DOWNLOAD_TEMPLATE")}</b>
                        </Typography>
                        <Typography variant="body2" className={classes.caption}>
                          {getLang(lang, "paragraph.TEMPLATE_DOWNLOAD_FILL_IN_PROMPTS_LUCKYDRAW") || 'Download the template file, fill in the serial numbers, and upload it back.'}
                        </Typography>
                        <Typography variant="body2" className={classes.caption}>
                          {getLang(lang, "paragraph.TEMPLATE_DOWNLOAD_FILL_IN_PROMPTS_LUCKYDRAW2") || 'Please ensure all serial numbers are valid and properly formatted.'}
                        </Typography>
                      </Box>
                      <Button
                        color="primary"
                        variant="contained"
                        onClick={handleTemplateDownload}
                        startIcon={<CloudUploadIcon />}
                      >
                        {getLang(lang, "label.DOWNLOAD_TEMPLATE")}
                      </Button>
                    </Box>
                  </Box>
                </Paper>
              </Box>
            </div>

            <Divider />
            <Box
              display="flex"
              justifyContent="flex-end"
              padding={2}
              alignItems="center"
            >
              <Button color="primary" onClick={() => setShowTemplateInput(false)} style={{ marginRight: 8 }}>
                {getLang(lang, "label.BACK")}
              </Button>
            </Box>
          </Paper>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="import-preset-winners-dialog"
      style={{
        zIndex: 1500, /* Ensure dialog appears on top */
      }}
      BackdropProps={{
        style: {
          backgroundColor: "rgba(0, 0, 0, 0.6)", /* More visible backdrop */
        },
      }}
    >
      <DialogContent style={{ padding: 0 }}>
        <Paper elevation={0} style={{ 
          maxHeight: "90vh",
          overflow: "auto",
          border: "1px solid #ccc",
          borderRadius: "4px",
        }}>
          <div className={classes.header}>
            <Typography variant="subtitle2" style={{ fontWeight: "bold" }}>
              {getLang(lang, "label.IMPORT_PRESET_WINNERS")}
            </Typography>
            <IconButton color="primary" onClick={handleClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
          <Divider />

          <div className={classes.form}>
            <Stepper activeStep={activeStep} orientation="vertical">
              {steps.map((step, index) => (
                <Step key={step.label}>
                  <StepLabel>{step.label}</StepLabel>
                  <StepContent>
                    <Typography>{step.description}</Typography>
                    <Box style={{ marginBottom: 16 }}>
                      <div>
                        {/* Step 1: Get Template */}
                        {index === 0 && (
                          <Box mt={2}>
                            <Typography variant="body2" className={classes.caption} style={{ marginBottom: 16 }}>
                              {getLang(lang, "paragraph.TEMPLATE_DOWNLOAD_FILL_IN_PROMPTS_LUCKYDRAW2") || 'Please ensure all serial numbers are valid and properly formatted.'}
                            </Typography>
                            <Button
                              color="primary"
                              variant="contained"
                              disabled={isValidating || isSubmitting}
                              onClick={handleTemplateDownload}
                              startIcon={<CloudUploadIcon />}
                              style={{ marginBottom: 16 }}
                            >
                              {getLang(lang, "label.DOWNLOAD_TEMPLATE")}
                            </Button>
                            <Box>
                              <Button
                                variant="contained"
                                onClick={handleNext}
                                style={{ marginTop: 8, marginRight: 8 }}
                              >
                                {getLang(lang, "label.CONTINUE")}
                              </Button>
                            </Box>
                          </Box>
                        )}

                        {/* Step 2: Upload CSV */}
                        {index === 1 && (
                          <Box mt={2}>
                            <Dropzone
                              ref={dropzoneRef}
                              noClick
                              maxFiles={1}
                              noKeyboard
                              accept={[".csv", ".xlsx", ".xls"]}
                              onDrop={(acceptedFiles) => {
                                if (acceptedFiles.length) {
                                  setFile(acceptedFiles[0]);
                                  handleFileChange(acceptedFiles[0]);
                                } else {
                                  setFile(null);
                                }
                              }}
                            >
                              {({ getRootProps, getInputProps, isDragActive }) => (
                                <div
                                  {...getRootProps()}
                                  className={clsx(classes.fileInput, {
                                    [classes.activeDropzone]: isDragActive,
                                  })}
                                  onClick={() => dropzoneRef.current.open()}
                                >
                                  <input {...getInputProps()} />
                                  <CloudUploadIcon
                                    style={{ fontSize: 40, color: "#3A4D54" }}
                                  />
                                  {file ? (
                                    <Typography variant="body1" style={{ marginTop: 8 }}>
                                      {file.name}
                                    </Typography>
                                  ) : (
                                    <Typography variant="body1" style={{ marginTop: 8 }}>
                                      {getLang(lang, "label.XLSX_DROP_OR_CLICK")}
                                    </Typography>
                                  )}
                                  {isFileProcessing && (
                                    <CircularProgress size={20} style={{ marginTop: 8 }} />
                                  )}
                                </div>
                              )}
                            </Dropzone>
                            <Box mt={1}>
                              <Button
                                variant="contained"
                                onClick={handleNext}
                                style={{ marginTop: 8, marginRight: 8 }}
                                disabled={!canProceedToStep(2)}
                              >
                                {getLang(lang, "label.CONTINUE")}
                              </Button>
                              <Button
                                disabled={index === 0}
                                onClick={handleBack}
                                style={{ marginTop: 8, marginRight: 8 }}
                              >
                                {getLang(lang, "label.BACK")}
                              </Button>
                            </Box>
                          </Box>
                        )}

                        {/* Step 3: Validate */}
                        {index === 2 && (
                          <Box mt={2}>
                            {!!showPreview && (
                              <Box mb={2}>
                                <Box mt={3} mb={1} display="flex" ml={1}>
                                  <Typography variant="body1">
                                    <b>{"Error" /* Use hardcoded text for testing */}</b>
                                  </Typography>
                                  <WarningIcon style={{ fontSize: 20, marginLeft: 8 }} />
                                </Box>
                                <TableContainer style={{ border: "1px solid #3a4d541a" }}>
                                  <Table className={classes.table}>
                                    <TableHead>
                                      <TableRow>
                                        {tableHeader.map((header, index) => (
                                          <TableCell
                                            key={index}
                                            style={{
                                              minWidth: 80,
                                              color: errorTitles.includes(index)
                                                ? "#ff0000"
                                                : "black",
                                              fontWeight: errorTitles.includes(index)
                                                ? "500"
                                                : "400",
                                            }}
                                          >
                                            {header}
                                          </TableCell>
                                        ))}
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {!!tableValues.length && (
                                        <>
                                          {!!errorField && errorField.row - 1 > 2 && (
                                            <TableRow>
                                              <TableCell colSpan={tableHeader.length}>
                                                <Box>...</Box>
                                              </TableCell>
                                            </TableRow>
                                          )}
                                          {getPreview().map((values, index) => (
                                            <TableRow key={index}>
                                              {values.map((val, i) => (
                                                <TableCell
                                                  key={i}
                                                  className={clsx({
                                                    [classes.errorCell]:
                                                      errorRow === index + 1 &&
                                                      (errorField?.column === i ||
                                                        errorField?.column === null),
                                                  })}
                                                  style={{
                                                    minWidth: 80,
                                                  }}
                                                >
                                                  {val}
                                                </TableCell>
                                              ))}
                                            </TableRow>
                                          ))}
                                          {!!errorField &&
                                            errorField.row + 2 < tableValues.length && (
                                              <TableRow>
                                                <TableCell colSpan={tableHeader.length}>
                                                  <Box>...</Box>
                                                </TableCell>
                                              </TableRow>
                                            )}
                                        </>
                                      )}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              </Box>
                            )}
                            
                            {/* Add Invalid Serial Numbers Table */}
                            {showInvalidSerialsTable && (
                              <Box mb={2}>
                                <InvalidSerialsTableComponent
                                  invalidSerialNumbers={invalidSerialNumbers}
                                  validSerialNumbers={validSerialNumbers}
                                  allSerialsStatus={allSerialsStatus}
                                  lang={lang}
                                />
                              </Box>
                            )}

                            <Box>
                              {!isValidFile ? (
                                <Button
                                  variant="contained"
                                  color="primary"
                                  classes={{ root: classes.publishButton }}
                                  disabled={!file || isValidating || isFileProcessing}
                                  onClick={handleValidate}
                                  style={{ marginTop: 8, marginRight: 8 }}
                                >
                                  {isValidating ? (
                                    <CircularProgress size={24} color="inherit" />
                                  ) : getLang(lang, "label.VALIDATE")}
                                </Button>
                              ) : (
                                <Button
                                  variant="contained"
                                  color="primary"
                                  classes={{ root: classes.publishButton }}
                                  disabled={isSubmitting || !isValidFile}
                                  onClick={handleSubmit}
                                  style={{ marginTop: 8, marginRight: 8 }}
                                >
                                  {isSubmitting ? (
                                    <CircularProgress size={24} color="inherit" />
                                  ) : (
                                    getLang(lang, "label.IMPORT")
                                  )}
                                </Button>
                              )}
                              <Button
                                disabled={index === 0}
                                onClick={handleBack}
                                style={{ marginTop: 8, marginRight: 8 }}
                              >
                                {getLang(lang, "label.BACK")}
                              </Button>
                            </Box>
                          </Box>
                        )}
                      </div>
                    </Box>
                  </StepContent>
                </Step>
              ))}
            </Stepper>
          </div>

          <Divider />
          <Box
            display="flex"
            justifyContent="flex-end"
            padding={2}
            alignItems="center"
          >
            <Button color="primary" onClick={handleClose}>
              {getLang(lang, "label.CANCEL")}
            </Button>
          </Box>
        </Paper>
      </DialogContent>
    </Dialog>
  );
} 